import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/images/Logo.png";
import PlayerPage from "./PlayerPage";
import {
  Play,
  Plus,
  Search,
  User,
  ChevronRight,
  Star,
  Calendar,
  Globe,
} from "lucide-react";

// Mock Data - African Heritage Films
const HERITAGE_FILMS = [
  {
    id: 1,
    title: "Sambizanga",
    originalYear: 1972,
    country: "Angola",
    director: "Sarah Maldoror",
    genre: "Historical Drama",
    rating: 4.8,
    duration: "1h 42m",
    restoredQuality: "4K",
    cloudinaryUrl: "https://res.cloudinary.com/djcdvfy7r/video/upload/v1774764595/The_New_Aston_Martin_Vantage___Engineered_for_real_drivers_kjgvfb.mp4",
    image: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=600&h=900&fit=crop",
    featured: true,
    description: "A powerful depiction of Angola's struggle for independence, following a woman's search for her imprisoned husband during the colonial era.",
    language: "Portuguese",
    significance: "Landmark of African revolutionary cinema",
  },
  {
    id: 2,
    title: "Touki Bouki",
    originalYear: 1973,
    country: "Senegal",
    director: "Djibril Diop Mambéty",
    genre: "Avant-Garde Drama",
    rating: 4.9,
    duration: "1h 29m",
    restoredQuality: "4K",
    cloudinaryUrl: "https://res.cloudinary.com/djcdvfy7r/video/upload/v1774764595/The_New_Aston_Martin_Vantage___Engineered_for_real_drivers_kjgvfb.mp4",
    image: "https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=600&h=900&fit=crop",
    description: "A young couple dreams of escaping Dakar for Paris in this surreal, visually stunning masterpiece of African cinema.",
    language: "Wolof/French",
    significance: "Pioneering work of African new wave cinema",
  },
  {
    id: 3,
    title: "Black Girl",
    originalYear: 1966,
    country: "Senegal",
    director: "Ousmane Sembène",
    genre: "Drama",
    rating: 4.7,
    duration: "1h 5m",
    restoredQuality: "2K",
    cloudinaryUrl: "https://res.cloudinary.com/djcdvfy7r/video/upload/v1774764595/The_New_Aston_Martin_Vantage___Engineered_for_real_drivers_kjgvfb.mp4",
    image: "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=600&h=900&fit=crop",
    description: "The first feature film by an African filmmaker to gain international recognition, exploring colonialism's psychological impact.",
    language: "French/Wolof",
    significance: "Birth of modern African cinema",
  },
  {
    id: 4,
    title: "Yeelen",
    originalYear: 1987,
    country: "Mali",
    director: "Souleymane Cissé",
    genre: "Epic Fantasy",
    rating: 4.8,
    duration: "1h 45m",
    restoredQuality: "4K",
    cloudinaryUrl: "https://res.cloudinary.com/djcdvfy7r/video/upload/v1774764595/The_New_Aston_Martin_Vantage___Engineered_for_real_drivers_kjgvfb.mp4",
    image: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=600&h=900&fit=crop",
    description: "A visually stunning epic rooted in Bambara mythology, following a young man's mystical journey.",
    language: "Bambara",
    significance: "Cannes Jury Prize winner",
  },
  {
    id: 5,
    title: "Mandabi",
    originalYear: 1968,
    country: "Senegal",
    director: "Ousmane Sembène",
    genre: "Social Satire",
    rating: 4.6,
    duration: "1h 30m",
    restoredQuality: "2K",
    cloudinaryUrl: "https://res.cloudinary.com/djcdvfy7r/video/upload/v1774764595/The_New_Aston_Martin_Vantage___Engineered_for_real_drivers_kjgvfb.mp4",
    image: "https://images.unsplash.com/photo-1440404653325-ab127d49abc1?w=600&h=900&fit=crop",
    description: "A satirical look at bureaucracy in post-independence Senegal through the story of a money order.",
    language: "Wolof",
    significance: "First African film in African language",
  },
  {
    id: 6,
    title: "Xala",
    originalYear: 1975,
    country: "Senegal",
    director: "Ousmane Sembène",
    genre: "Political Satire",
    rating: 4.7,
    duration: "2h 3m",
    restoredQuality: "4K",
    cloudinaryUrl: "https://res.cloudinary.com/djcdvfy7r/video/upload/v1774764595/The_New_Aston_Martin_Vantage___Engineered_for_real_drivers_kjgvfb.mp4",
    image: "https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=600&h=900&fit=crop",
    description: "A biting satire of African bourgeoisie and neo-colonialism through a businessman cursed with impotence.",
    language: "Wolof/French",
    significance: "Masterwork of political cinema",
  },
];

const COLLECTIONS = [
  {
    id: 7,
    title: "The Battle of Algiers",
    originalYear: 1966,
    country: "Algeria/Italy",
    director: "Gillo Pontecorvo",
    genre: "War/Historical",
    rating: 4.9,
    duration: "2h 1m",
    restoredQuality: "4K",
    cloudinaryUrl: "https://res.cloudinary.com/djcdvfy7r/video/upload/v1774764595/The_New_Aston_Martin_Vantage___Engineered_for_real_drivers_kjgvfb.mp4",
    image: "https://images.unsplash.com/photo-1574267432644-f610c39ce1c6?w=600&h=900&fit=crop",
    description: "Powerful depiction of the Algerian struggle for independence from French colonial rule.",
    language: "Arabic/French",
    significance: "Golden Lion winner, revolutionary cinema landmark",
  },
  {
    id: 8,
    title: "Muna Moto",
    originalYear: 1975,
    country: "Cameroon",
    director: "Jean-Pierre Dikongué Pipa",
    genre: "Romance/Drama",
    rating: 4.5,
    duration: "1h 30m",
    restoredQuality: "2K",
    cloudinaryUrl: "https://res.cloudinary.com/djcdvfy7r/video/upload/v1774764595/The_New_Aston_Martin_Vantage___Engineered_for_real_drivers_kjgvfb.mp4",
    image: "https://images.unsplash.com/photo-1594908900066-3f47337549d8?w=600&h=900&fit=crop",
    description: "A tragic love story exploring tradition versus modernity in post-colonial Cameroon.",
    language: "French",
    significance: "First Cameroonian feature film",
  },
];

const CATEGORIES = [
  "Restored Classics",
  "Revolutionary Cinema",
  "West African Masters",
  "East African Heritage",
  "North African Gems",
  "Southern African Stories",
  "Pre-Independence",
  "1960s-70s Golden Age",
];

const HomePage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("Restored Classics");

  const handlePlayClick = (film) => {
    navigate("/player", { state: { content: film } });
  };

  const handleDetailClick = (film) => {
    navigate("/detail", { state: { content: film } });
  };
 useEffect(() => {
  try {
    const adEl = document.querySelector(".adsbygoogle");
    // Only push if the slot hasn't been filled yet
    if (adEl && !adEl.getAttribute("data-adsbygoogle-status")) {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    }
  } catch (e) {
    console.error("AdSense error:", e);
  }
}, []);

  return (
    <div className="fade-in-up">
      <div className="header kente-border">
        <div className="logo-container">
          <div className="logo">
            <img src={logo} alt="" style={{ width: "140px", height: "auto" }} />
          </div>
          <div className="logo-subtitle">Heritage Cinema</div>
        </div>
        <div className="header-actions">
          <button className="icon-btn" onClick={() => navigate("/discover")}>
            <Search size={20} />
          </button>
          <button className="icon-btn" onClick={() => navigate("/profile")}>
            <User size={20} />
          </button>
        </div>
      </div>

      <div className="featured-section">
        <div className="featured-card">
          <img
            src={HERITAGE_FILMS[0].image}
            alt={HERITAGE_FILMS[0].title}
            className="featured-image"
          />
          <div className="featured-gradient" />
          <div className="featured-content">
            <span className="heritage-badge">🎬 Restored Classic</span>
            <h1 className="featured-title">{HERITAGE_FILMS[0].title}</h1>
            <div className="featured-meta">
              <span className="rating">
                <Star size={15} fill="currentColor" />
                {HERITAGE_FILMS[0].rating}
              </span>
              <span className="meta-item">
                <Calendar size={14} />
                {HERITAGE_FILMS[0].originalYear}
              </span>
              <span className="meta-item">
                <Globe size={14} />
                {HERITAGE_FILMS[0].country}
              </span>
              <span className="restored-badge">{HERITAGE_FILMS[0].restoredQuality} RESTORED</span>
            </div>
            <p className="featured-description">{HERITAGE_FILMS[0].description}</p>
            <div className="featured-actions">
              <button
                className="btn-primary"
                onClick={() => handlePlayClick(HERITAGE_FILMS[0])}
              >
                <Play size={22} fill="currentColor" />
                Watch Now
              </button>
              <button className="btn-secondary">
                <Plus size={26} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="categories">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            className={`category-chip ${selectedCategory === cat ? "active" : ""}`}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="content-section">
        <div className="section-header">
          <h2 className="section-title">West African Masters</h2>
          <button className="see-all">
            Explore <ChevronRight size={16} />
          </button>
        </div>
        <div className="content-grid">
          {HERITAGE_FILMS.slice(1, 5).map((film) => (
            <div
              key={film.id}
              className="content-card"
              onClick={() => handleDetailClick(film)}
            >
              <img
                src={film.image}
                alt={film.title}
                className="content-card-image"
              />
              <span className="quality-badge">{film.restoredQuality}</span>
              <div className="content-card-info">
                <div className="content-card-title">{film.title}</div>
                <div className="content-card-meta">
                  {film.originalYear} • {film.country}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="content-section">
        <div className="section-header">
          <h2 className="section-title">Revolutionary Cinema</h2>
          <button className="see-all">
            Explore <ChevronRight size={16} />
          </button>
        </div>
        <div className="content-grid">
          {COLLECTIONS.map((film) => (
            <div
              key={film.id}
              className="content-card"
              onClick={() => handleDetailClick(film)}
            >
              <img
                src={film.image}
                alt={film.title}
                className="content-card-image"
              />
              <span className="quality-badge">{film.restoredQuality}</span>
              <div className="content-card-info">
                <div className="content-card-title">{film.title}</div>
                <div className="content-card-meta">
                  {film.originalYear} • {film.country}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* ✅ Ad banner inside the single return, at the bottom */}
      <ins className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client="ca-pub-XXXXXXXX"
        data-ad-slot="XXXXXXXX"
        data-ad-format="auto">
      </ins>
    </div>
  );
};

export default HomePage;